/**
 * Customer-facing wording for canonical session states and charger
 * availability that this app owns.
 *
 * The explanation of WHY a session changed (cause, what happens next, what to
 * do) arrives from the API with the snapshot (`snapshot.message`) so the app,
 * push and email say the same thing. What lives here is the wording the app
 * needs even without it: state labels, billing and freshness lines, and a
 * plain fallback per state.
 *
 * The app has no localization framework. This catalogue is the seam for one:
 * every string a canonical surface shows is here, keyed by locale, as a whole
 * sentence or a function of plain values. `copy.test.js` requires every
 * locale to define every key. Never put a backend enum name in a string.
 */
import { SESSION_STATE as S } from './canonical';

export const DEFAULT_LOCALE = 'en';

const en = {
  state: {
    [S.PREPARING]: 'Getting ready',
    [S.CHARGING]: 'Charging',
    [S.PAUSED]: 'Paused',
    [S.RESUMING]: 'Restarting',
    [S.COMPLETED]: 'Completed',
    [S.ENDED_EARLY]: 'Ended early',
    [S.FAILED_TO_START]: 'Did not start',
    [S.STATUS_UNKNOWN]: 'Updates unavailable',
    [S.CHARGER_UNAVAILABLE]: 'Charger unavailable',
  },
  heading: {
    [S.PREPARING]: 'Getting ready to charge',
    [S.CHARGING]: 'Charging',
    [S.PAUSED]: 'Charging paused',
    [S.RESUMING]: 'Charging is restarting',
    [S.COMPLETED]: 'Charging complete',
    [S.ENDED_EARLY]: 'Charging ended early',
    [S.FAILED_TO_START]: 'Charging did not start',
    [S.STATUS_UNKNOWN]: 'Live updates unavailable',
    [S.CHARGER_UNAVAILABLE]: 'Charger unavailable',
  },
  // Used only when the API sent a snapshot without its message.
  fallbackBody: {
    [S.PREPARING]: 'Your charge is being set up.',
    [S.CHARGING]: 'Your vehicle is charging.',
    [S.PAUSED]: 'Charging is paused.',
    [S.RESUMING]: 'Charging is restarting.',
    [S.COMPLETED]: 'Charging has finished.',
    [S.ENDED_EARLY]: 'This session ended before it was expected to.',
    [S.FAILED_TO_START]: 'No energy was delivered.',
    [S.STATUS_UNKNOWN]: 'We cannot confirm what the charger is doing right now. Your vehicle may still be charging.',
    [S.CHARGER_UNAVAILABLE]: 'This charger cannot start a charge right now.',
  },
  billing: {
    pending: 'Final cost is being calculated',
    pendingShort: 'Cost pending',
    notCharged: 'Not charged',
    energySoFar: 'Energy so far',
    costSoFar: 'Cost so far',
    energy: 'Energy delivered',
    cost: 'Total cost',
    estimate: 'Not final',
  },
  freshness: {
    stale: 'This may be out of date.',
    lastUpdated: (time) => `Last updated ${time}`,
    lastConfirmed: (label, time) => `Last confirmed: ${label} at ${time}`,
    reconciling: 'We are back in contact with the charger and confirming your session.',
    refreshing: 'Checking for the latest…',
    refresh: 'Check again',
    refreshFailed: 'We could not refresh this session. Showing the last confirmed details.',
  },
  recovered: 'Charging resumed',
  restored: 'Live updates restored',
  stopRequested: 'Stop requested. Waiting for the charger to confirm.',
  support: (code) => `Support reference: ${code}`,
  timeline: 'What happened',
  action: {
    RETRY: 'Try again',
    RECONNECT_VEHICLE: 'Start a new session',
    UPDATE_PAYMENT_METHOD: 'Try again',
    USE_ANOTHER_CHARGER: 'Find another charger',
    CONTACT_SUPPORT: 'Contact support',
  },
  availability: {
    AVAILABLE: 'Available',
    IN_USE: 'In use',
    TEMPORARILY_UNAVAILABLE: 'Temporarily unavailable',
    OFFLINE: 'Offline',
    OUT_OF_SERVICE: 'Out of service',
  },
  notFound: {
    title: 'Session not available',
    body: 'This session could not be found, or you no longer have access to it.',
  },
};

export const CATALOG = Object.freeze({ en });
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(CATALOG));

export function copyFor(locale = DEFAULT_LOCALE) {
  return CATALOG[locale] ?? CATALOG[DEFAULT_LOCALE];
}

/**
 * How a state looks. Tone is paired with an icon and the label wherever it is
 * used — colour is never the only signal.
 *   tone     ui/Badge tone
 *   notice   Common/Notice variant for the status panel
 *   urgent   announced assertively (role="alert")
 */
const STATE_STYLE = Object.freeze({
  [S.PREPARING]: { tone: 'info', notice: 'info', urgent: false },
  [S.CHARGING]: { tone: 'success', notice: 'success', urgent: false },
  [S.PAUSED]: { tone: 'warning', notice: 'warning', urgent: false },
  [S.RESUMING]: { tone: 'info', notice: 'info', urgent: false },
  [S.COMPLETED]: { tone: 'success', notice: 'success', urgent: false },
  [S.ENDED_EARLY]: { tone: 'danger', notice: 'danger', urgent: true },
  [S.FAILED_TO_START]: { tone: 'danger', notice: 'danger', urgent: true },
  [S.STATUS_UNKNOWN]: { tone: 'warning', notice: 'warning', urgent: false },
  [S.CHARGER_UNAVAILABLE]: { tone: 'warning', notice: 'warning', urgent: false },
});
const UNKNOWN_STYLE = STATE_STYLE[S.STATUS_UNKNOWN];

/** Label + tone for a state. An unrecognised state reads as "updates unavailable", never as completed. */
export function stateCopy(sessionState, locale) {
  const c = copyFor(locale);
  const known = Object.hasOwn(STATE_STYLE, sessionState);
  const state = known ? sessionState : S.STATUS_UNKNOWN;
  return { state, label: c.state[state], heading: c.heading[state], ...(known ? STATE_STYLE[state] : UNKNOWN_STYLE) };
}

const AVAILABILITY_TONE = Object.freeze({
  AVAILABLE: 'success', IN_USE: 'warning', TEMPORARILY_UNAVAILABLE: 'warning', OFFLINE: 'neutral', OUT_OF_SERVICE: 'danger',
});

/** Label + tone for an API availability code, or null when the API sent none this build knows. */
export function availabilityCopy(code, locale) {
  if (!Object.hasOwn(AVAILABILITY_TONE, code ?? '')) return null;
  return { code, label: copyFor(locale).availability[code], tone: AVAILABILITY_TONE[code] };
}

/**
 * Everything the status panel says for a snapshot. The API's message is used
 * when present; the app's own fallback otherwise.
 */
export function presentSnapshot(snapshot, locale) {
  const c = copyFor(locale);
  const style = stateCopy(snapshot?.sessionState, locale);
  const m = snapshot?.message ?? null;
  const safetyHeading = m?.heading && typeof m.heading === 'string' ? m.heading : null;
  return {
    ...style,
    heading: safetyHeading ?? style.heading,
    body: (m?.body && typeof m.body === 'string') ? m.body : c.fallbackBody[style.state],
    notice: typeof m?.notice === 'string' ? m.notice : null,
    actionCode: typeof m?.actionCode === 'string' ? m.actionCode : (snapshot?.recovery?.clientAction ?? 'NONE'),
    actionLabel: typeof m?.actionLabel === 'string' ? m.actionLabel
      : c.action[snapshot?.recovery?.clientAction] ?? null,
    supportCode: typeof snapshot?.supportCode === 'string' ? snapshot.supportCode : null,
  };
}

/**
 * Energy and cost lines. Pending figures are labelled as not final and are
 * never shown as zero; a session that never delivered energy is "not charged".
 */
export function presentBilling(snapshot, locale) {
  const c = copyFor(locale).billing;
  const b = snapshot?.billing;
  if (!b) return null;
  if (b.status === 'NOT_APPLICABLE') return { status: 'NOT_APPLICABLE', final: true, label: c.notCharged, energyKwh: null, totalCost: null, currency: b.currency ?? null };
  if (b.status === 'CONFIRMED') {
    return { status: 'CONFIRMED', final: true, label: null, energyLabel: c.energy, costLabel: c.cost, energyKwh: b.energyKwh ?? null, totalCost: b.totalCost ?? null, currency: b.currency ?? null };
  }
  return {
    status: 'PENDING', final: false, label: c.pending, shortLabel: c.pendingShort, estimateLabel: c.estimate,
    energyLabel: c.energySoFar, costLabel: c.costSoFar,
    energyKwh: b.provisionalEnergyKwh ?? null, totalCost: b.provisionalTotalCost ?? null, currency: b.currency ?? null,
  };
}
